import { UUID } from "../outings";
import {
  FREESTYLE_MAX_SOURCE_BYTES,
  FREESTYLE_SOURCE_TYPES,
  type FreestyleSourceType,
} from "./upload-contract";

export const FREESTYLE_ACTIVITIES = {
  ski: "Skiing",
  snowboard: "Snowboarding",
  mtb: "Mountain biking",
  surf: "Surfing",
  skate: "Skating",
  wakeboard: "Wakeboarding",
  other: "Another activity",
} as const;
export type FreestyleActivity = keyof typeof FREESTYLE_ACTIVITIES;
export type ClipDetails = {
  caption: string;
  trick: string;
  activity: FreestyleActivity;
  filmedOn: string;
};
export type FreestyleClip = ClipDetails & {
  id: string;
  revision: number;
  createdAt: string;
  state:
    | "awaiting"
    | "queued"
    | "creating"
    | "processing"
    | "ready"
    | "failed"
    | "complete";
  failure: string | null;
  bytes: number;
  sha256: string;
  contentType: FreestyleSourceType;
  uploadUntil: string;
  duration: number | null;
  audience: "Only me";
};
export type FreestyleCommand =
  | (ClipDetails & {
      action: "reserve";
      id: string;
      sha256: string;
      bytes: number;
      contentType: FreestyleSourceType;
      consent: "private-upload-v1";
    })
  | (ClipDetails & { action: "edit"; id: string; revision: number })
  | { action: "delete" | "complete" | "grant" | "play"; id: string };
export const freestyleObject = (
  value: unknown,
): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
export function validClipDetails(
  value: unknown,
): value is ClipDetails & Record<string, unknown> {
  if (!freestyleObject(value)) return false;
  return (
    typeof value.caption === "string" &&
    value.caption.trim().length > 0 &&
    value.caption.length <= 500 &&
    typeof value.trick === "string" &&
    value.trick.trim().length > 0 &&
    value.trick.length <= 80 &&
    typeof value.activity === "string" &&
    Object.hasOwn(FREESTYLE_ACTIVITIES, value.activity) &&
    typeof value.filmedOn === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value.filmedOn) &&
    value.filmedOn >= "1900-01-01" &&
    Number.isFinite(Date.parse(value.filmedOn)) &&
    new Date(value.filmedOn).toISOString().slice(0, 10) === value.filmedOn &&
    value.filmedOn <=
      new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)
  );
}
export function validFreestyleCommand(
  value: unknown,
): value is FreestyleCommand {
  if (
    !freestyleObject(value) ||
    typeof value.id !== "string" ||
    !UUID.test(value.id)
  )
    return false;
  if (["delete", "complete", "grant", "play"].includes(String(value.action)))
    return Object.keys(value).length === 2;
  if (!validClipDetails(value)) return false;
  if (value.action === "edit")
    return (
      Object.keys(value).length === 7 &&
      Number.isSafeInteger(value.revision) &&
      Number(value.revision) > 0
    );
  return (
    value.action === "reserve" &&
    Object.keys(value).length === 10 &&
    value.consent === "private-upload-v1" &&
    typeof value.sha256 === "string" &&
    /^[a-f0-9]{64}$/.test(value.sha256) &&
    Number.isSafeInteger(value.bytes) &&
    Number(value.bytes) > 0 &&
    Number(value.bytes) <= FREESTYLE_MAX_SOURCE_BYTES &&
    FREESTYLE_SOURCE_TYPES.includes(value.contentType as FreestyleSourceType)
  );
}
export function readOwnedClips(value: unknown, owner: string): FreestyleClip[] {
  if (
    !freestyleObject(value) ||
    value.owner !== owner ||
    !Array.isArray(value.clips) ||
    value.clips.length > 20
  )
    throw new Error("Your clips could not be checked. Refresh to retry.");
  for (const clip of value.clips) {
    if (
      !freestyleObject(clip) ||
      !validClipDetails(clip) ||
      typeof clip.id !== "string" ||
      !UUID.test(clip.id) ||
      clip.audience !== "Only me" ||
      ![
        "awaiting",
        "queued",
        "creating",
        "processing",
        "ready",
        "failed",
        "complete",
      ].includes(String(clip.state)) ||
      !Number.isSafeInteger(clip.revision) ||
      Number(clip.revision) < 1 ||
      typeof clip.createdAt !== "string" ||
      !Number.isFinite(Date.parse(clip.createdAt)) ||
      typeof clip.uploadUntil !== "string" ||
      !Number.isFinite(Date.parse(clip.uploadUntil)) ||
      typeof clip.sha256 !== "string" ||
      !/^[a-f0-9]{64}$/.test(clip.sha256) ||
      !Number.isSafeInteger(clip.bytes) ||
      Number(clip.bytes) < 1 ||
      Number(clip.bytes) > FREESTYLE_MAX_SOURCE_BYTES ||
      !FREESTYLE_SOURCE_TYPES.includes(
        clip.contentType as FreestyleSourceType,
      ) ||
      (clip.failure !== null && typeof clip.failure !== "string") ||
      (clip.duration !== null &&
        (typeof clip.duration !== "number" ||
          clip.duration < 1 ||
          clip.duration > 60))
    )
      throw new Error("Your clips could not be checked. Refresh to retry.");
  }
  return value.clips as FreestyleClip[];
}
export function clipStatus(clip: FreestyleClip): string {
  if (clip.state === "awaiting") return "Ready to upload";
  if (clip.state === "ready") return "Ready to watch";
  if (["failed", "complete"].includes(clip.state))
    return clip.failure === "duration"
      ? "Choose a video between 1 and 60 seconds"
      : clip.failure === "expired"
        ? "Upload expired. Remove this draft to start again."
        : "This clip could not be processed. Remove it to try another video.";
  return clip.state === "creating"
    ? "Confirming video processing"
    : "Preparing your video. You can leave this page.";
}
